import React, { type FC, createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'

import { noop } from 'helpers/utils'
import Modal, { type ModalReturnValue, type Props as ModalProps } from 'components/Modal'
import { type BaseProps } from 'types'

type ShowFunction = (options: Omit<ModalProps, 'open'>) => void

interface State extends ModalProps {
  show: ShowFunction
  hide: () => void
}

const initialState: State = {
  titleText: '',
  open: false,
  show: noop,
  hide: noop,
  primaryBtnTitle: '',
}

export const ModalContext = createContext<State>(initialState)

const ModalProvider: FC<BaseProps> = ({ children }) => {
  const show: ShowFunction = ({
    titleText,
    children,
    onClose,
    primaryBtnTitle,
    secondaryBtnTitle,
  }) => {
    setState(prevState => {
      return {
        ...prevState,
        titleText,
        open: true,
        children,
        onClose,
        primaryBtnTitle,
        secondaryBtnTitle,
      }
    })
  }

  const hide = (): void => {
    setState(prevState => {
      return {
        ...prevState,
        open: false,
      }
    })
  }

  const handleClose = (returnValue: ModalReturnValue): void => {
    setState(prevState => {
      return {
        ...prevState,
        open: false,
      }
    })
    if (state.onClose) state.onClose(returnValue)
  }

  const [state, setState] = useState<State>({
    ...initialState,
    show,
    hide,
  })

  return (
    <ModalContext.Provider value={state}>
      {children}
      {createPortal(
        <Modal
          titleText={state.titleText}
          open={state.open}
          onClose={handleClose}
          primaryBtnTitle={state.primaryBtnTitle}
          secondaryBtnTitle={state.secondaryBtnTitle}
        >
          {state.children}
        </Modal>,
        document.body,
      )}
    </ModalContext.Provider>
  )
}

export const useModal = (): State => {
  return useContext(ModalContext)
}

export default ModalProvider
